import { cx } from '@linaria/core';
import { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useEscapeKey } from '../Hooks/useEscapeKey';
import { PAPER_HEIGHTS } from '../theme';
import { PopoverOrigin, useAnchoredPosition } from './anchoredPosition';
import { useMountTransition } from './transitions';
import "./Popover.linaria.module.css!=!./Popover?./Popover.linaria.module.css?css=H4sIAAAAAAAAA42S3W7jIBCF7/cpfLNSKoUIYqdO6cMgzI9NjRkC+C/WvvuqbexY3Zu9nOE7hzMMJ5461rWMuLf0GqrFQzTJgKPaTEq+//l1+gR0zoz0ui4W46JKFK8HXcticuP1+hTyKoLtk9qQD1bbedTd4sG4pAJSg3IpUgdug1K7XpDAP+31bW1bpXfXmplNQ333bun4hBpl6iZRwa04EIx/ZyjLz356WWmLGbkMt3EMP8oFBhW0hRFNtDFSKrdJCCNjX0d3+VE+JTPlfYJNcGZEX4gq9RcRRQBrUaUaPhgIVIBL3Gz2Z8mI7Vvnyh/lUnHR1gF6J5EAC4EOPBwQ2rU99ypss+WaVa3CebtUEKQKlPgpi2CNzL6VMvBRBSTNYOROd/lgZC4KsPohRIFL00da+GllSswIr+TtLSwVTCg2XML4CGT5rAJKIzzam3FpGckjSCWX/QCfDxB4TCipKW1wW6z7Bc+FSfNzxSkw8urFlaQlBe6ihtDRKLhVB3wqL5tFLxiJ2gzw4L4/4cMuO2PcxUz0lRGoUnejwgGfimOGjxk+nY8ZeTlu7v8B73K3VemG+5ab7HM3Jc89/JP7S/8X84+mlXEDAAA="
export const POPOVER_TRANSITION = 200;
export type { PopoverOrigin };
const rootStyles = "atm_mk_1n9t6rb atm_f3_idpfg4";
const backdropStyles = "atm_mk_stnw88 atm_f3_idpfg4";

// a hover-opened popover must not sit between the pointer and the anchor,
// or mounting it would fire the anchor's pointerleave and close it again
const passiveStyles = "atm_mj_glywfm";
const paperStyles = "atm_mk_1n9t6rb atm_tk_idpfg4 atm_fq_idpfg4 atm_iy_xvgzpn atm_l0_15vqwwr atm_l1_1wugsn5 atm_l2_1f51e7f atm_2d_1luknn7 atm_3f_bke03k atm_5j_1y44olf atm_70_1abdq9r atm_7l_13soded atm_k4_idpfg4 atm_tr_16pc81t atm_uc_1sfivot";
const paperEnteredStyles = "atm_k4_kb7nvz atm_tr_1h7a3po";
export interface PopoverProps {
  anchorEl: HTMLElement | null;
  anchorOrigin?: PopoverOrigin;
  children: ReactNode;
  /** lands on the paper — the surface every call site actually wants to style */
  className?: string;
  'data-testid'?: string;
  /** keep the paper under the anchor and shrink it to the viewport */
  fitBelowAnchor?: boolean;
  id?: string;
  /** widen the paper to at least the anchor, the way a dropdown list reads */
  matchAnchorWidth?: boolean;
  onClose?(): void;
  open: boolean;
  /** let pointer events through to the page: no backdrop, no close on click */
  passive?: boolean;
  transformOrigin?: PopoverOrigin;
  zIndex?: number;
}
const Popover = (props: PopoverProps) => {
  const {
    anchorEl,
    anchorOrigin = {
      vertical: 'bottom',
      horizontal: 'center'
    },
    children,
    className,
    fitBelowAnchor = false,
    id,
    matchAnchorWidth = false,
    onClose,
    open,
    passive = false,
    transformOrigin = {
      vertical: 'top',
      horizontal: 'center'
    },
    zIndex = PAPER_HEIGHTS.popover,
    'data-testid': dataTestId
  } = props;
  const {
    mounted,
    entered
  } = useMountTransition(open, POPOVER_TRANSITION);
  const {
    position,
    targetRef
  } = useAnchoredPosition(anchorEl, mounted, anchorOrigin, transformOrigin, {
    fitBelowAnchor
  });
  useEscapeKey(open, onClose);
  if (!mounted) return null;
  return createPortal(<div className={cx('mfc-popover', rootStyles, passive && passiveStyles)} style={{
    zIndex
  }}>
            {!passive && <div className={cx('popover-backdrop', backdropStyles)} onClick={onClose} />}
            <div className={cx('popover-paper', paperStyles, entered && paperEnteredStyles, className)} data-testid={dataTestId} id={id} ref={targetRef} style={{
      left: position?.left,
      maxHeight: position?.maxHeight,
      maxWidth: position?.maxWidth,
      minWidth: matchAnchorWidth ? anchorEl?.offsetWidth : undefined,
      top: position?.top,
      transformOrigin: `${transformOrigin.horizontal} ${transformOrigin.vertical}`,
      visibility: position ? undefined : 'hidden'
    }}>
                {children}
            </div>
        </div>, document.body);
};
export default Popover;